import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Anchor, Box, Text } from "@mantine/core";
import { notifyError, notifySuccess } from "../../utils/notify";
import moment from "moment";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useTransaction } from "../../context/TransactionContext";
import {
  GetTransactionsResponse,
  Payment,
  TransactionResponse,
} from "../../types/transactions";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { formatDateTime, DEFAULT_TIMEZONE } from "../../utils/timeUtils";
import RefundPaymentModal from "../checkout/RefundPaymentModal";
import TransactionDetailModal from "../calendar/CalendarView/components/TransactionDetailModal/TransactionDetailModal";
import useReceiptPrint from "../common/useReceiptPrint";
import EmailReceiptModal from "./shared/EmailReceiptModal";
import SummaryStrip from "./shared/SummaryStrip";
import SalesToolbar from "./shared/SalesToolbar";
import { SalesTable, SalesTableRow } from "./shared/SalesTable";
import SalesPagination from "./shared/SalesPagination";
import StatusCell, { transactionStatusKey } from "./shared/StatusCell";
import TransactionDetailDrawer from "./shared/TransactionDetailDrawer";
import VoidTransactionModal from "./shared/VoidTransactionModal";
import { useSalesDrawerSlot } from "./shared/salesDrawerSlot";
import { matchesTagFilter, SalesFilters, toIso } from "./shared/filters";
import { AppointmentTag } from "../../types/appointmentTags";
import SalesDateRangePill from "./shared/SalesDateRangePill";
import { DateRangePreset } from "../../types/reports";
import {
  resolveReportsRange,
  reportsRangeReady,
} from "./shared/reportsDateRange";
import {
  clientName,
  lineItemNames,
  netTotal,
  refundedTotal,
} from "./shared/salesDerivations";
import styles from "./shared/sales.module.css";

const COLUMN_TEMPLATE = "104px 122px 150px 108px 1.6fr 108px 34px";
const MIN_WIDTH = 900;

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open only" },
  { value: "closed", label: "Closed only" },
  { value: "refunded", label: "Refunded" },
  { value: "partial", label: "Partial" },
  { value: "voided", label: "Voided" },
];
const DEFAULT_STATUS = "all";

interface TransactionsSubTabProps {
  /** Bumped by the shell after a New Sale completes, to force a refetch. */
  refreshNonce?: number;
  /** Filter + search state is lifted to SalesPage so it survives tab switches. */
  filters: SalesFilters;
  onFiltersChange: (next: SalesFilters) => void;
  query: string;
  onQueryChange: (q: string) => void;
  /** Reports-style date range (presets + custom), lifted to SalesPage. */
  datePreset: DateRangePreset;
  customStart: string | null;
  customEnd: string | null;
  onDateChange: (
    preset: DateRangePreset,
    start: string | null,
    end: string | null,
  ) => void;
  /** The location's tag catalog for the Tags filter (hidden when empty). */
  tagOptions?: AppointmentTag[];
}

/**
 * Sales → Transactions (one row per transaction). Assembled entirely from the
 * shared page pattern (summary strip, toolbar, table, status cell, detail
 * slide-over). Refunds are payment-level: each payment in the slide-over has a
 * Refund action that opens the existing RefundPaymentModal.
 */
function TransactionsSubTab({
  refreshNonce,
  filters,
  onFiltersChange,
  query,
  onQueryChange,
  datePreset,
  customStart,
  customEnd,
  onDateChange,
  tagOptions = [],
}: TransactionsSubTabProps) {
  const { businessInfo } = useSalon() as {
    businessInfo?: { id: string; salon_name?: string; timezone?: string };
  };
  // Render every datetime in the business location's timezone (not the
  // viewer's browser), falling back to the app default when the salon record
  // hasn't loaded a timezone yet.
  const timezone = businessInfo?.timezone || DEFAULT_TIMEZONE;
  const { printReceipt, receipt } = useReceiptPrint();
  const { salesLoading, salesError, fetchSales } = useTransaction();
  // Own rows (transaction-grain) so the shared context state — which the
  // Payments tab overwrites with tender-grain rows — can't bleed in as
  // duplicate rows for a multi-tender transaction.
  const [rows, setRows] = useState<GetTransactionsResponse[]>([]);
  const { sendReceipt } = useApp();
  // Full-page-height drawer host owned by SalesPage; the drawer portals into it.
  const drawerSlot = useSalesDrawerSlot();

  const [openId, setOpenId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
  const [voidTarget, setVoidTarget] = useState<GetTransactionsResponse | null>(
    null,
  );
  const [emailing, setEmailing] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  // Appointment/transaction details opened in place from the drawer's ref link.
  // Kept in sync with the modal's updates (checkout, void) so it re-renders.
  const [apptTxn, setApptTxn] = useState<
    GetTransactionsResponse | TransactionResponse | null
  >(null);

  // Monotonic request id so a slow response can't overwrite the list after a
  // newer fetch (rapid date/filter switch, or refreshNonce firing mid-flight).
  const reqIdRef = useRef(0);
  const load = useCallback(async () => {
    if (!businessInfo?.id) return;
    if (!reportsRangeReady(datePreset, customStart, customEnd)) return;
    const [start, end] = resolveReportsRange(
      datePreset,
      customStart,
      customEnd,
    );
    const params = `start_date=${toIso(start)}&end_date=${toIso(end)}`;
    const myReqId = ++reqIdRef.current;
    const result = await fetchSales(businessInfo.id, "transactions", params);
    if (myReqId !== reqIdRef.current) return; // superseded by a newer fetch
    setRows(result ?? []);
  }, [businessInfo?.id, datePreset, customStart, customEnd, fetchSales]);

  useEffect(() => {
    load();
  }, [load, refreshNonce]);

  // Reset to the first page whenever the result set changes shape.
  useEffect(() => {
    setPage(1);
  }, [query, filters, datePreset, customStart, customEnd]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^#/, "");
    const txnFilter = filters.txn.trim().toLowerCase().replace(/^#/, "");
    const clientFilter = filters.client.trim().toLowerCase();
    const last4Filter = filters.last4.trim();

    return rows
      .filter((row) => !row.is_future_deposit_only)
      .filter((row) => {
        if (filters.status === "all") return true;
        const key = transactionStatusKey(
          row.transaction?.transaction_status,
          row.transaction?.refund_status,
        );
        return key === filters.status;
      })
      .filter((row) => {
        if (q.length === 0) return true;
        const ref = (row.transaction?.reference_id || "").toLowerCase();
        return ref.includes(q) || clientName(row).toLowerCase().includes(q);
      })
      .filter((row) =>
        txnFilter.length === 0
          ? true
          : (row.transaction?.reference_id || "")
              .toLowerCase()
              .includes(txnFilter),
      )
      .filter((row) =>
        clientFilter.length === 0
          ? true
          : clientName(row).toLowerCase().includes(clientFilter),
      )
      .filter((row) =>
        last4Filter.length === 0
          ? true
          : (row.payments || []).some((p) => p.card_last4 === last4Filter),
      )
      .filter((row) => matchesTagFilter(row.tags, filters.tagIds))
      .sort((a, b) =>
        moment(b.transaction?.created_dt).diff(
          moment(a.transaction?.created_dt),
        ),
      );
  }, [rows, query, filters]);

  const summary = useMemo(() => {
    let net = 0;
    let refunds = 0;
    // Voided transactions stay listed but are out of every report-style total.
    const counted = filteredRows.filter(
      (row) => row.transaction?.transaction_status !== "void",
    );
    counted.forEach((row) => {
      net += netTotal(row);
      refunds += refundedTotal(row);
    });
    const count = counted.length;
    return [
      { label: "Net sales", value: formatCurrency(net) },
      { label: "Transactions", value: count.toLocaleString() },
      {
        label: "Average",
        value: formatCurrency(count > 0 ? net / count : 0),
      },
      {
        label: "Refunds",
        value: formatCurrency(refunds),
        danger: refunds > 0,
      },
    ];
  }, [filteredRows]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  const openRow = useMemo(
    () =>
      openId
        ? (filteredRows.find((r) => r.transaction?.id === openId) ?? null)
        : null,
    [openId, filteredRows],
  );

  const handleRowClick = (row: GetTransactionsResponse) => {
    const id = row.transaction?.id ?? null;
    setOpenId((cur) => (cur === id ? null : id));
  };

  const stripePayment = useMemo(
    () => openRow?.payments.find((p) => !!p.stripe_payment_intent_id) ?? null,
    [openRow],
  );

  const sendEmailReceipt = useCallback(
    async (email: string) => {
      if (!stripePayment) return;
      setEmailing(true);
      try {
        await sendReceipt(stripePayment.id, email);
        notifySuccess(`The receipt was emailed to ${email}.`, {
          title: "Receipt sent",
        });
        setEmailModalOpen(false);
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Please try again.", {
          title: "Couldn't send receipt",
        });
      } finally {
        setEmailing(false);
      }
    },
    [stripePayment, sendReceipt],
  );

  return (
    <Box>
      {salesError && (
        <Text size="xs" c="red" mb="sm">
          Couldn&apos;t load transactions.{" "}
          <Anchor onClick={load} component="button" type="button" size="xs">
            Retry
          </Anchor>
        </Text>
      )}

      <Box>
        <Box>
          <SummaryStrip stats={summary} />

          <SalesToolbar
            query={query}
            onQueryChange={onQueryChange}
            filters={filters}
            onFiltersChange={onFiltersChange}
            defaultStatus={DEFAULT_STATUS}
            statusOptions={STATUS_OPTIONS}
            count={pagedRows.length}
            total={filteredRows.length}
            itemLabel="transactions"
            searchPlaceholder="Search transaction #, client"
            loading={salesLoading}
            tagOptions={tagOptions}
            dateControl={
              <SalesDateRangePill
                preset={datePreset}
                customStart={customStart}
                customEnd={customEnd}
                onChange={onDateChange}
              />
            }
          />

          <SalesTable
            columnTemplate={COLUMN_TEMPLATE}
            minWidth={MIN_WIDTH}
            headers={[
              { label: "Transaction Id" },
              { label: "Date" },
              { label: "Client" },
              { label: "Status" },
              { label: "Line items" },
              { label: "Total", align: "right" },
              { label: "" },
            ]}
            loading={salesLoading}
            isEmpty={filteredRows.length === 0}
            emptyText="No transactions match these filters."
          >
            {pagedRows.map((row) => {
              const txn = row.transaction;
              const net = netTotal(row);
              const statusKey = transactionStatusKey(
                txn?.transaction_status,
                txn?.refund_status,
              );
              return (
                <SalesTableRow
                  key={txn?.id}
                  active={openId === txn?.id}
                  onClick={() => handleRowClick(row)}
                >
                  <Box className={styles.cellNum}>
                    {txn?.reference_id || "—"}
                  </Box>
                  <Box className={styles.cellNum}>
                    {txn?.created_dt
                      ? formatDateTime(
                          txn.created_dt,
                          timezone,
                          "MMM D · h:mm A",
                        )
                      : "—"}
                  </Box>
                  <Box className={styles.cellEllipsis}>{clientName(row)}</Box>
                  <Box>
                    <StatusCell status={statusKey} />
                  </Box>
                  <Box className={styles.cellEllipsis}>
                    {lineItemNames(row) || "—"}
                  </Box>
                  <Box className={`${styles.cellRight} ${styles.cellNum}`}>
                    {formatCurrency(net)}
                  </Box>
                </SalesTableRow>
              );
            })}
          </SalesTable>

          <SalesPagination
            page={page}
            pageSize={pageSize}
            total={filteredRows.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            itemLabel="transactions"
          />
        </Box>

        {openRow &&
          drawerSlot &&
          createPortal(
            <TransactionDetailDrawer
              overlay={false}
              row={openRow}
              timezone={timezone}
              onClose={() => setOpenId(null)}
              onRefund={setRefundTarget}
              onPrint={() => printReceipt(openRow)}
              onEmail={() => setEmailModalOpen(true)}
              emailDisabled={!stripePayment}
              emailLoading={emailing}
              onVoid={() => setVoidTarget(openRow)}
              onOpenAppointment={
                openRow.transaction?.id ? () => setApptTxn(openRow) : undefined
              }
            />,
            drawerSlot,
          )}
      </Box>

      {refundTarget && (
        <RefundPaymentModal
          opened
          payment={refundTarget}
          onClose={() => setRefundTarget(null)}
          onSuccess={load}
        />
      )}

      {voidTarget?.transaction?.id && businessInfo?.id && (
        <VoidTransactionModal
          opened
          businessLocationId={businessInfo.id}
          transactionId={voidTarget.transaction.id}
          referenceId={voidTarget.transaction.reference_id}
          onClose={() => setVoidTarget(null)}
          onSuccess={load}
        />
      )}

      {apptTxn && (
        <TransactionDetailModal
          opened
          transaction={apptTxn}
          onClose={() => setApptTxn(null)}
          onTransactionUpdated={(updated) => {
            setApptTxn(updated);
            load();
          }}
        />
      )}

      <EmailReceiptModal
        opened={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        clientEmail={openRow?.client?.email || undefined}
        onSend={sendEmailReceipt}
        loading={emailing}
      />

      {receipt}
    </Box>
  );
}

export default TransactionsSubTab;
